import { useState } from "react";
import { t, tf } from "@/lib/i18n";
import { CheckCircle2, XCircle, RotateCcw, Sparkles } from "lucide-react";

const QUESTIONS = [
  { q: "What's the best thing to do with dry leaves in your society?", options: ["Burn them", "Compost them", "Throw in the drain"], a: 1, why: "Burning leaves adds smoke to the air we breathe. Composting turns them into soil food." },
  { q: "Before giving plastic bottles to a kabadiwala, you should…", options: ["Rinse and dry them", "Fill them with wet waste", "Crush them with food inside"], a: 0, why: "Clean, dry plastic is easier to recycle and often fetches a better price." },
  { q: "Where should old batteries go?", options: ["Kitchen dustbin", "Kept separate for safe disposal", "In the compost pit"], a: 1, why: "Batteries contain chemicals that can leak into soil and water." },
  { q: "Carrying a cloth bag when shopping helps because…", options: ["It looks nice", "It avoids single-use plastic bags", "Shops require it"], a: 1, why: "Every reusable bag can replace many single-use plastic bags over its life." },
  { q: "Which uses the least energy for a short 1 km errand?", options: ["Car", "Scooter", "Walking or cycling"], a: 2, why: "Walking and cycling cost nothing, emit nothing and keep you healthy." },
];

export default function ClimateQuiz() {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const done = idx >= QUESTIONS.length;

  const pick = (o) => {
    if (picked !== null) return;
    setPicked(o);
    if (o === QUESTIONS[idx].a) setScore((s) => s + 1);
  };
  const next = () => { setPicked(null); setIdx((n) => n + 1); };
  const restart = () => { setIdx(0); setPicked(null); setScore(0); };

  if (done) {
    return (
      <div className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-8 text-center" data-testid="quiz-result">
        <Sparkles className="mx-auto text-[var(--marigold)]" size={40} />
        <div className="mt-3 font-display text-4xl font-bold">{score}/{QUESTIONS.length}</div>
        <p className="mt-2 text-[var(--ink-soft)]">{score === QUESTIONS.length ? t("Perfect! You're a climate-action pro.") : t("Every right answer is a habit you can start today.")}</p>
        <button onClick={restart} className="btn-leaf mt-6 text-sm" data-testid="quiz-restart"><RotateCcw size={15} /> {t("Try again")}</button>
      </div>
    );
  }

  const Q = QUESTIONS[idx];
  return (
    <div className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-5 sm:p-7" data-testid="climate-quiz">
      <div className="text-xs font-semibold text-[var(--ink-soft)]">{tf("Question {n} of {total}", { n: idx + 1, total: QUESTIONS.length })}</div>
      <h3 className="mt-2 text-xl sm:text-2xl font-bold leading-snug" data-testid="quiz-question">{t(Q.q)}</h3>
      <div className="mt-5 space-y-2.5">
        {Q.options.map((o, i) => {
          const state = picked === null ? "" : i === Q.a ? "border-[var(--leaf)] bg-[var(--leaf-soft)]" : i === picked ? "border-[var(--terracotta)] bg-[#FBE9E3]" : "opacity-60";
          return (
            <button key={t(o)} onClick={() => pick(i)} data-testid={`quiz-option-${i}`}
              className={`w-full text-left px-4 py-3.5 rounded-2xl border-2 border-[rgba(20,40,26,.1)] font-medium transition-colors hover:border-[var(--leaf)] flex items-center justify-between gap-3 ${state}`}>
              {t(o)}
              {picked !== null && i === Q.a && <CheckCircle2 size={18} className="text-[var(--leaf)] shrink-0" />}
              {picked === i && i !== Q.a && <XCircle size={18} className="text-[var(--terracotta)] shrink-0" />}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div className="mt-4 fade-up">
          <p className="text-sm text-[var(--ink-soft)]" data-testid="quiz-explanation">{t(Q.why)}</p>
          <button onClick={next} className="btn-leaf mt-4 text-sm" data-testid="quiz-next">{idx + 1 === QUESTIONS.length ? t("See my score") : t("Next question")}</button>
        </div>
      )}
    </div>
  );
}
